/**
 * Admin Partners API Service
 * Handles partner/mitra management
 */

import { get, post, patch, del, API_BASE_URL, getAuthToken } from "../client";
import { CreatorTier } from "../../../types";
import { PaginationMeta, ImportResult } from "./affiliates";

export interface Creator {
  id: string;
  name: string;
  platform: string;
  socialMediaUrl?: string;
  followersCount: string;
  joinedDate: string;
  tier: CreatorTier;
  email?: string;
  whatsapp?: string;
  bankType?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  totalCommission?: number;
  status?: "active" | "inactive";
}

// === Response Types ===

interface ApiPartner {
  id: string;
  userId: string;
  socialMediaHandle: string | null;
  socialMediaUrl: string | null;
  followersCount: number;
  tier: CreatorTier;
  status: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
  };
  bankType: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  total_commission?: number | string;
}

interface PartnersResponse {
  message: string;
  data: {
    meta: PaginationMeta;
    data: ApiPartner[];
  };
}

export interface PaginatedPartners {
  data: Creator[];
  meta: PaginationMeta;
}

interface PartnerResponse {
  message: string;
  data: {
    partner: ApiPartner;
    user: {
      id: string;
      fullName: string;
      email: string;
      phone: string;
    };
  };
}

// === Request Types ===

export interface CreatePartnerRequest {
  fullName: string;
  email: string;
  phone: string;
  socialMediaHandle: string;
  socialMediaUrl: string;
  followersCount?: number;
  bankType: string;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
}

// === Transform Functions ===

function transformPartner(api: ApiPartner): Creator {
  const user = api.user || {
    id: (api as any).user_id || "",
    fullName: (api as any).full_name || "",
    email: (api as any).email || "",
    phone: (api as any).phone || "",
  };

  return {
    id: api.id,
    name: user.fullName,
    email: user.email,
    platform: api.socialMediaHandle || (api as any).social_media_handle || "Unknown",
    socialMediaUrl: api.socialMediaUrl || (api as any).social_media_url || undefined,
    followersCount: Number(api.followersCount || (api as any).followers_count || 0).toLocaleString("id-ID"),
    joinedDate: "-",
    tier: api.tier || "Nano",
    whatsapp: user.phone,
    bankType: api.bankType || (api as any).bank_type || undefined,
    bankName: api.bankName || (api as any).bank_name || undefined,
    bankAccountNumber:
      api.bankAccountNumber || (api as any).bank_account_number || undefined,
    bankAccountName:
      api.bankAccountName || (api as any).bank_account_name || undefined,
    totalCommission: Number(api.total_commission || 0),
    status: api.status === "active" ? "active" : "inactive",
  };
}

// === API Functions ===

/**
 * Get all partners with pagination and search
 */
export async function getPartners(
  page = 1,
  search = "",
  perPage = 5
): Promise<PaginatedPartners> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("perPage", String(perPage));
  if (search) {
    params.append("search", search);
  }
  const response = await get<PartnersResponse>(
    `/admin/partners?${params.toString()}`
  );

  const payload = (response as any).data || {};
  const rows = Array.isArray(payload.data) ? payload.data : [];
  const meta = payload.meta || {};
  const total = Number(meta.total || rows.length || 0);
  const parsedPerPage = Number(meta.perPage || perPage || 5);
  const currentPage = Number(meta.currentPage || page || 1);
  const lastPage = Number(
    meta.lastPage || Math.max(1, Math.ceil(total / Math.max(parsedPerPage, 1)))
  );

  return {
    data: rows.map(transformPartner as any),
    meta: {
      total,
      perPage: parsedPerPage,
      currentPage,
      lastPage,
      firstPage: 1,
      firstPageUrl: undefined,
      lastPageUrl: "",
      nextPageUrl: currentPage < lastPage ? "" : null,
      previousPageUrl: currentPage > 1 ? "" : null,
    },
  };
}

/**
 * Create a new partner
 */
export async function createPartner(
  data: CreatePartnerRequest
): Promise<Creator> {
  const response = await post<PartnerResponse>("/admin/partners", data);
  const apiPartner = { ...response.data.partner, user: response.data.user };
  return transformPartner(apiPartner);
}

/**
 * Update a partner
 */
export async function updatePartner(
  id: string,
  data: Partial<CreatePartnerRequest> & { status?: string }
): Promise<Creator> {
  const response = await patch<PartnerResponse>(`/admin/partners/${id}`, data);
  const apiPartner = { ...response.data.partner, user: response.data.user };
  return transformPartner(apiPartner);
}

/**
 * Delete a partner
 */
export async function deletePartner(id: string): Promise<void> {
  await del(`/admin/partners/${id}`);
}

// Ensure the del path matches the route
export async function apiDeletePartner(id: string): Promise<void> {
  await del(`/admin/partners/${id}`);
}

// === Import Functions ===

/**
 * Download partner import template
 */
export function getPartnerTemplateUrl(): string {
  return `${API_BASE_URL}/admin/imports/partners/template`;
}

/**
 * Import partners from Excel file
 */
export async function importPartners(file: File): Promise<ImportResult> {
  const formData = new FormData();
  formData.append("file", file);

  const token = localStorage.getItem("auth_token");
  const response = await fetch(`${API_BASE_URL}/admin/imports/partners`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  }
  );

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || "Failed to import partners");
  }
  return result.data;
}

/**
 * Bulk delete partners
 */
export async function bulkDeletePartners(ids: string[]): Promise<any> {
  console.log('bulkDeletePartners called with ids:', ids);
  const response = await fetch(`${API_BASE_URL}/admin/partners/bulk`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify({ ids }),
  });

  const result = await response.json();
  console.log('bulkDeletePartners response:', response.status, result);
  if (!response.ok) {
    throw new Error(result.message || "Failed to bulk delete partners");
  }
  return result;
}
